import { forwardRef } from 'react';
import { cn } from '@/utils/styles';

export function H1({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h1
      className={cn(
        'scroll-m-20 font-monserrat text-3xl font-extrabold tracking-tight lg:text-[56px]',
        className,
      )}>
      {children}
    </h1>
  );
}

export function H2({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={cn(
        'scroll-m-20 font-monserrat text-[28px] font-semibold tracking-tight first:mt-0 lg:text-5xl',
        className,
      )}>
      {children}
    </h2>
  );
}

export function H3({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={cn(
        'scroll-m-20 font-monserrat text-xl font-semibold leading-[135%] md:leading-[110%] lg:text-4xl',
        className,
      )}>
      {children}
    </h3>
  );
}

export const H4 = forwardRef<
  HTMLHeadingElement,
  {
    children: React.ReactNode;
    className?: string;
  }
>(({ children, className }, ref) => {
  return (
    <h4
      ref={ref}
      className={cn(
        'scroll-m-20 font-monserrat text-2xl font-semibold leading-[40px] tracking-tight md:text-3xl',
        className,
      )}>
      {children}
    </h4>
  );
});

export function H5({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h5
      className={cn(
        'scroll-m-20 font-monserrat text-xl font-semibold leading-[140%] tracking-tight md:leading-[90%] lg:text-2xl',
        className,
      )}>
      {children}
    </h5>
  );
}

export function H6({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h6
      className={cn(
        'scroll-m-20 font-monserrat text-base font-semibold leading-[21px] tracking-tight lg:text-xl',
        className,
      )}>
      {children}
    </h6>
  );
}

export function P({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'font-lato text-sm font-light leading-normal md:text-base',
        className,
      )}>
      {children}
    </p>
  );
}

export function Blockquote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="mt-6 border-l-2 pl-6 italic">{children}</blockquote>
  );
}

export function List({ list }: { list: string[] }) {
  return (
    <ul className="my-6 ml-6 list-disc font-thin [&>li]:mt-2">
      {list.map((item, index) => (
        <li className="text-sm !font-thin" key={index}>
          {item}
        </li>
      ))}
    </ul>
  );
}

export function Lead({ children }: { children: React.ReactNode }) {
  return <p className="text-xl text-muted-foreground">{children}</p>;
}

export function Large({ children }: { children: React.ReactNode }) {
  return <div className="text-lg font-semibold">{children}</div>;
}

export function Small({ children }: { children: React.ReactNode }) {
  return <small className="text-sm font-medium leading-none">{children}</small>;
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
